import React from "react";
import { Empty, Table } from "antd";
import {
  BATCH_REPORT_GENERATE_STATUS,
  BATCH_REPORT_TEST_STATUS,
} from "../../../../utils/statusConfig";
import {
  DATA_OPS_RESULT_STATUS,
  EMPTY_CELL_VALUE,
  OVERDUE_ELLIPSIS_COLUMN,
  formatHasRcaCellValue,
  formatOverdueCellValue,
  renderOverdueEllipsisCell,
  renderOverduePhaseEllipsisCell,
  resolveOverdueReasonDisplayLabel,
} from "../utils/overdueUtils";
import {
  DATA_OPS_TABLE_PAGINATION,
  DATA_OPS_TABLE_SCROLL_Y,
  sumColumnWidths,
} from "../utils/utils";
import { formatRcaDisplayDate } from "../utils/rcaUtils";

const formatBooleanCell = (value) => {
  if (value == null || value === "") {
    return EMPTY_CELL_VALUE;
  }
  return value ? "Yes" : "No";
};

const renderResultStatusCell = (resultStatus) => {
  if (resultStatus == null || resultStatus === "") {
    return formatOverdueCellValue(resultStatus);
  }
  const key = String(resultStatus).trim();
  const batchStatus = BATCH_REPORT_TEST_STATUS[key];
  if (batchStatus) {
    return <span style={batchStatus.style}>{batchStatus.label}</span>;
  }
  const statusObj = DATA_OPS_RESULT_STATUS[key];
  if (!statusObj) {
    return formatOverdueCellValue(resultStatus);
  }
  return <span style={{ color: statusObj.color }}>{statusObj.label}</span>;
};

const renderReportStatusCell = (reportStatus) => {
  if (reportStatus == null || reportStatus === "") {
    return formatOverdueCellValue(reportStatus);
  }
  const statusObj = BATCH_REPORT_GENERATE_STATUS[String(reportStatus)];
  if (!statusObj) {
    return formatOverdueCellValue(reportStatus);
  }
  return <span style={statusObj.style}>{statusObj.label}</span>;
};

const buildOverdueColumns = (reasonLabelById, showExtendedColumns) => {
  const columns = [
    {
      title: "Customer",
      dataIndex: "customer_company",
      key: "customer_company",
      width: 160,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverdueEllipsisCell,
    },
    {
      title: "Customer ID",
      dataIndex: "customer_id",
      key: "customer_id",
      width: 120,
      render: formatOverdueCellValue,
    },
    {
      title: "WO",
      dataIndex: "workorder_id",
      key: "workorder_id",
      width: 130,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverdueEllipsisCell,
    },
    {
      title: "GBID",
      dataIndex: "genista_id",
      key: "genista_id",
      width: 150,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverdueEllipsisCell,
    },
    {
      title: "Test Category",
      dataIndex: "category_name",
      key: "category_name",
      width: 140,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverdueEllipsisCell,
    },
    {
      title: "Test",
      dataIndex: "test_name",
      key: "test_name",
      width: 200,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverdueEllipsisCell,
    },
    {
      title: "Phase",
      dataIndex: "phase",
      key: "phase",
      width: 180,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: renderOverduePhaseEllipsisCell,
    },
    {
      title: "Overdue reason",
      dataIndex: "reason",
      key: "reason",
      width: 160,
      ellipsis: OVERDUE_ELLIPSIS_COLUMN,
      render: (_value, record) => (
        renderOverdueEllipsisCell(resolveOverdueReasonDisplayLabel(record, reasonLabelById))
      ),
    },
  ];

  if (!showExtendedColumns) {
    return columns;
  }

  return [
    ...columns,
    {
      title: "Result Status",
      dataIndex: "result_status",
      key: "result_status",
      width: 120,
      render: renderResultStatusCell,
    },
    {
      title: "Report Status",
      dataIndex: "report_status",
      key: "report_status",
      width: 130,
      render: renderReportStatusCell,
    },
    {
      title: "Received Date",
      dataIndex: "received_date",
      key: "received_date",
      width: 120,
      render: formatOverdueCellValue,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 120,
      render: formatOverdueCellValue,
    },
    {
      title: "TAT",
      dataIndex: "tat",
      key: "tat",
      width: 80,
      render: formatOverdueCellValue,
    },
    {
      title: "Days Overdue",
      dataIndex: "days_overdue",
      key: "days_overdue",
      width: 120,
      render: formatOverdueCellValue,
    },
    {
      title: "Is Retest",
      dataIndex: "is_retest",
      key: "is_retest",
      width: 100,
      render: formatBooleanCell,
    },
    {
      title: "Has RCA",
      dataIndex: "has_rca",
      key: "has_rca",
      width: 140,
      render: formatHasRcaCellValue,
    },
    {
      title: "RCA Initiated",
      dataIndex: "rca_created_date",
      key: "rca_created_date",
      width: 140,
      render: formatRcaDisplayDate,
    },
  ];
};

export default function OverdueTable({
  rows,
  loading,
  reasonLabelById = null,
  onViewRow,
  showExtendedColumns = false,
}) {
  const columns = buildOverdueColumns(reasonLabelById, showExtendedColumns);
  const tableScrollX = sumColumnWidths(columns);

  return (
    <Table
      bordered
      className="DataOpsAnalytics__Table"
      size="middle"
      rowKey="rowKey"
      loading={loading}
      columns={columns}
      dataSource={rows}
      onRow={onViewRow
        ? (record) => ({
          onClick: () => onViewRow(record),
          className: "DataOpsAnalytics__ClickableRow",
        })
        : undefined}
      pagination={DATA_OPS_TABLE_PAGINATION}
      scroll={{ x: tableScrollX, y: DATA_OPS_TABLE_SCROLL_Y }}
      locale={{
        emptyText: (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        ),
      }}
    />
  );
}
